import * as THREE from 'three';
import type { SkyMood } from '../../weather/weather-types.js';
import { configureSunShadow } from '../perf-profile.js';

export interface SceneLighting {
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  warmBar: THREE.PointLight;
  patio: THREE.PointLight;
  applySkyMood: (mood: SkyMood) => void;
  update: (t: number) => void;
}

const SKY_PRESETS: Record<
  SkyMood,
  { bg: number; fog: number; sun: number; sunI: number; hemiSky: number; hemiGround: number; hemiI: number }
> = {
  clear: { bg: 0x8aa8c8, fog: 0x9ab4d0, sun: 0xffe8c8, sunI: 1.48, hemiSky: 0xfff8ee, hemiGround: 0x5a6a78, hemiI: 0.72 },
  cloudy: { bg: 0x8a96a8, fog: 0x9aa4b4, sun: 0xf0ece8, sunI: 0.95, hemiSky: 0xf0f2f4, hemiGround: 0x6a7078, hemiI: 0.82 },
  rain: { bg: 0x6a7888, fog: 0x7a8898, sun: 0xd8e0e8, sunI: 0.55, hemiSky: 0xd8dde2, hemiGround: 0x5a6268, hemiI: 0.9 },
  dusk: { bg: 0xc88868, fog: 0xd8a080, sun: 0xffb878, sunI: 1.05, hemiSky: 0xffe0c8, hemiGround: 0x6a5048, hemiI: 0.65 },
  night: { bg: 0x2a3545, fog: 0x323c4c, sun: 0x99aacc, sunI: 0.38, hemiSky: 0x4a5a6a, hemiGround: 0x1a2028, hemiI: 0.58 },
};

export function setupRestaurantLighting(scene: THREE.Scene, mobile = false): SceneLighting {
  scene.background = new THREE.Color(SKY_PRESETS.clear.bg);
  scene.fog = new THREE.Fog(SKY_PRESETS.clear.fog, 42, 95);

  const hemi = new THREE.HemisphereLight(0xfff8ee, 0x5a6a78, 0.72);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xffe8c8, 1.48);
  sun.position.set(16, 28, 12);
  sun.castShadow = true;
  configureSunShadow(sun, mobile);
  sun.shadow.bias = -0.0002;
  sun.shadow.normalBias = 0.02;
  const s = 26;
  sun.shadow.camera.left = -s;
  sun.shadow.camera.right = s;
  sun.shadow.camera.top = s;
  sun.shadow.camera.bottom = -s;
  scene.add(sun);

  const warmBar = new THREE.PointLight(0xffb070, 0.55, 18, 2);
  warmBar.position.set(-14, 3.2, -2);
  scene.add(warmBar);

  const kitchen = new THREE.PointLight(0xfff0dd, 0.45, 16, 2);
  kitchen.position.set(8, 3.5, -10);
  scene.add(kitchen);

  const grill = new THREE.PointLight(0xff6622, 0.7, 10, 2);
  grill.position.set(-2, 2.2, -10);
  scene.add(grill);

  const patio = new THREE.PointLight(0xffd9a0, 0.35, 22, 2);
  patio.position.set(10, 4, 2);
  scene.add(patio);

  let mood: SkyMood = 'clear';

  return {
    sun,
    hemi,
    warmBar,
    patio,
    applySkyMood(next: SkyMood) {
      mood = next;
      const p = SKY_PRESETS[next];
      scene.background = new THREE.Color(p.bg);
      if (scene.fog) (scene.fog as THREE.Fog).color.setHex(p.fog);
      sun.color.setHex(p.sun);
      sun.intensity = p.sunI;
      hemi.color.setHex(p.hemiSky);
      hemi.groundColor.setHex(p.hemiGround);
      hemi.intensity = p.hemiI;
      warmBar.intensity = next === 'night' ? 0.72 : next === 'dusk' ? 0.58 : 0.48;
      patio.intensity = next === 'night' ? 0.55 : 0.35;
    },
    update(t: number) {
      grill.intensity = 0.55 + Math.sin(t * 3.2) * 0.15;
      warmBar.intensity += Math.sin(t * 0.7) * 0.02;
      if (mood === 'night') {
        sun.position.x = 8 + Math.sin(t * 0.05) * 2;
        sun.position.y = 12;
      }
    },
  };
}
